import { Box, Tab } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { TabContext, TabList, TabPanel } from '@mui/lab';

import { WizardStep } from 'domain/entities/WizardStep';
import NavigationBar from 'infrastructure/components/NavigationBar';
import PageLayout from 'infrastructure/components/PageLayout';
import {
  selectExperienceType,
  selectLocation,
  selectActiveInstructorTab,
} from 'infrastructure/redux/slices/bookingWizard.selector';
import {
  setExperienceType,
  setActiveInstructorTab,
} from 'infrastructure/redux/slices/bookingWizard.slice';
import { useAppDispatch, useAppSelector } from 'infrastructure/redux/store/hooks';
import LocationSelect from 'infrastructure/targets/web/modules/bookingWizard/components/LocationSelect';
import useCaseSelectInstructorWeb from 'application/modules/bookingWizard/useCases/hooks/useCaseSelectInstructorWeb';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';

import { StyledContentContainer } from './style';
import LessonByCoach from './components/LessonByCoach';
import LessonByDate from './components/LessonByDate';

// Local enum to ensure runtime stability
enum TabTypes {
  LessonByDate = 'lessonByDate',
  LessonByCoach = 'lessonByCoach',
}

interface ISelectInstructorStep {
  stepName: WizardStep;
}

const SelectInstructorStep: FC<ISelectInstructorStep> = ({ stepName }) => {
  const dispatch = useAppDispatch();
  const t = useTranslationPrefix('book.select_instructor');
  const selectedLocationId = useAppSelector(selectLocation);
  const selectedExperienceType = useAppSelector(selectExperienceType);
  const savedActiveTab = useAppSelector(selectActiveInstructorTab);

  // Initialize activeTab from Redux state or default to LessonByCoach
  const [activeTab, setActiveTab] = useState(
    savedActiveTab ||
      (selectedExperienceType &&
      Object.values(TabTypes).includes(selectedExperienceType as TabTypes)
        ? selectedExperienceType
        : TabTypes.LessonByCoach),
  );

  const {
    result: instructorsList,
    inProgress: instructorsLoading,
    getInstructors,
    nextStep,
    prevStep,
    preselectInstructor,
    preselectLocation,
    preselectReservationDate,
    userEmail: email,
    getInstructorsByDate,
    availableInstructorsOnDate,
    areAvailableInstructorsOnDateLoading,
  } = useCaseSelectInstructorWeb(stepName);
  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    // Save the active tab to Redux state so we can restore it when user navigates back
    dispatch(setActiveInstructorTab({ activeInstructorTab: newValue }));
    // Set the experience type when switching tabs to ensure proper flow routing
    dispatch(setExperienceType({ experienceType: newValue }));
  };

  useEffect(() => {
    // Ensure the experience type is set to match the active tab on component mount
    dispatch(setExperienceType({ experienceType: activeTab }));
    // Also save the active tab to Redux state
    dispatch(setActiveInstructorTab({ activeInstructorTab: activeTab }));
  }, [dispatch]);

  return (
    <PageLayout pageTitle={t('heading')} shouldFadeIn>
      <NavigationBar backButtonCallback={prevStep}>
        <LocationSelect selectedSimLocationId={selectedLocationId} />
      </NavigationBar>
      <StyledContentContainer>
        <TabContext value={activeTab}>
          <Box sx={{ paddingBottom: 6 }}>
            <TabList onChange={handleChange} indicatorColor="primary" centered variant="fullWidth">
              <Tab
                label={t('search_by_date')}
                value={TabTypes.LessonByDate}
                sx={{
                  textTransform: 'none',
                  fontWeight: activeTab === TabTypes.LessonByDate ? 600 : 400,
                  fontSize: 16,
                }}
              />
              <Tab
                label={t('search_by_coach')}
                value={TabTypes.LessonByCoach}
                sx={{
                  textTransform: 'none',
                  fontWeight: activeTab === TabTypes.LessonByCoach ? 600 : 400,
                  fontSize: 16,
                }}
              />
            </TabList>
          </Box>
          <TabPanel value={TabTypes.LessonByCoach}>
            <LessonByCoach
              instructorsList={instructorsList}
              instructorsLoading={instructorsLoading}
              getInstructors={getInstructors}
              nextStep={nextStep}
              preselectInstructor={preselectInstructor}
              preselectLocation={preselectLocation}
              preselectReservationDate={preselectReservationDate}
              email={email}
            />
          </TabPanel>
          <TabPanel value={TabTypes.LessonByDate}>
            <LessonByDate
              instructorsList={availableInstructorsOnDate}
              instructorsLoading={areAvailableInstructorsOnDateLoading}
              getInstructors={getInstructorsByDate}
              nextStep={nextStep}
              email={email}
            />
          </TabPanel>
        </TabContext>
      </StyledContentContainer>
    </PageLayout>
  );
};

export default SelectInstructorStep;
